import { Instagram, Youtube, Facebook, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import vpmLogo from '@/assets/vpm-logo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/_vantagepointmedia?igsh=MWJod281cHN0cHlnNQ==', label: 'Instagram' },
    { icon: Youtube, href: 'https://youtube.com/@shaytalton8709?si=1SrS0o0ta09LsUmE', label: 'YouTube' },
    { icon: Facebook, href: 'https://www.facebook.com/share/1C7d4c4Xcn/?mibextid=wwXIfr', label: 'Facebook' },
  ];

  const quickLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'Book a Discovery Call', href: '/booking' },
  ];

  const serviceLinks = [
    { label: 'Social Media Management', href: '/services/social-media' },
    { label: 'Video Production', href: '/services/video-production' },
    { label: 'Content Strategy', href: '/services/content-strategy' },
    { label: 'Brand Photography', href: '#services' },
    { label: 'Ad Campaign Management', href: '#services' },
  ];

  return (
    <footer className="bg-foreground text-background">
      {/* Entity Clarity Block for AI */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-6 sm:pb-8 border-b border-background/10">
        <div className="max-w-3xl">
          <h2 className="font-display font-semibold text-lg sm:text-xl text-background mb-3">About Vantage Point Media</h2>
          <p className="text-background/70 text-sm sm:text-base leading-relaxed">
            Vantage Point Media is a video production and social media agency serving the Columbus, Ohio area. We help med spas, dentists, financial advisors, real estate professionals, surgeons, and small businesses grow through cinematic video content, strategic social media management, and data-driven content strategy. Unlike large agencies, we provide a personal, boutique experience with dedicated teams, transparent pricing, and measurable results.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-1">
            <div className="mb-4 sm:mb-6">
              <img 
                src={vpmLogo} 
                alt="Vantage Point Media - Video Production & Social Media Agency Columbus Ohio" 
                className="h-10 sm:h-12 w-auto"
              />
            </div>
            <p className="text-background/70 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              Central Ohio's premier video production and social media agency. Helping professionals build trust and attract high-value clients through cinematic content and strategic marketing.
            </p>
            <div className="flex gap-3 sm:gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={`Follow Vantage Point Media on ${social.label}`}
                  className="w-9 h-9 sm:w-10 sm:h-10 bg-background/10 rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg mb-4 sm:mb-6">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith('/') ? (
                    <Link
                      to={link.href}
                      className="text-background/70 hover:text-primary transition-colors text-sm sm:text-base"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-background/70 hover:text-primary transition-colors text-sm sm:text-base"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg mb-4 sm:mb-6">Services</h4>
            <ul className="space-y-2 sm:space-y-3">
              {serviceLinks.map((service) => (
                <li key={service.label}>
                  {service.href.startsWith('/') ? (
                    <Link
                      to={service.href}
                      className="text-background/70 hover:text-primary transition-colors text-sm sm:text-base"
                    >
                      {service.label}
                    </Link>
                  ) : (
                    <a
                      href={service.href}
                      className="text-background/70 hover:text-primary transition-colors text-sm sm:text-base"
                    >
                      {service.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 lg:col-span-1">
            <h4 className="font-display font-semibold text-base sm:text-lg mb-4 sm:mb-6">Contact</h4>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-background/70 text-sm sm:text-base">
                  Columbus, Ohio Area
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                <a
                  href="mailto:shay@vantagepointmediagroup.com"
                  className="text-background/70 hover:text-primary transition-colors text-xs sm:text-base break-all"
                >
                  shay@vantagepointmediagroup.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                <a
                  href="tel:+16146250865"
                  className="text-background/70 hover:text-primary transition-colors text-sm sm:text-base"
                >
                  (614) 625-0865
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-background/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-background/60 text-xs sm:text-sm">
              © {currentYear} Vantage Point Media. All rights reserved.
            </p>
            <div className="flex gap-4 sm:gap-6 text-xs sm:text-sm">
              <a href="#" className="text-background/60 hover:text-primary transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-background/60 hover:text-primary transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
